<app-page-header
  title="Auftragsdetails"
  subtitle="Alle Informationen zu einem Reflex-Auftrag"
  icon="receipt_long"
  [showBackButton]="true"
  variant="gradient">
</app-page-header>

<!-- nlc-style Content Grid -->
<div class="nlc-container nlc-slide-up">
  <!-- Loading State -->
  <div *ngIf="isLoading" class="loading-container">
    <mat-icon class="loading-icon">hourglass_empty</mat-icon>
    <p>Lade Auftragsdetails...</p>
  </div>

  <!-- Error State -->
  <div *ngIf="error && !isLoading" class="error-container">
    <mat-icon class="error-icon">error_outline</mat-icon>
    <p>{{ error }}</p>
    <button mat-raised-button color="primary" (click)="loadOrderDetails()">
      Erneut versuchen
    </button>
  </div>

  <!-- Order Details -->
  <div *ngIf="!isLoading && !error && (orderDetails$ | async) as order" class="order-details-grid">
    <!-- Order Information Card -->
    <div class="nlc-card">
      <div class="nlc-card-header">
        <div class="nlc-card-icon">
          <mat-icon>receipt_long</mat-icon>
        </div>
        <div class="nlc-card-title-section">
          <h3>Auftragsinformationen</h3>
          <p>Auftrags Nr: {{ order.orderNo }}</p>
        </div>
      </div>
      <div class="nlc-card-content">
        <div class="nlc-info-row highlight-row">
          <span class="nlc-info-label">Auftragsnummer</span>
          <span class="nlc-info-value">{{ order.orderNo }}</span>
        </div>
        <div class="nlc-info-row">
          <span class="nlc-info-label">Status</span>
          <span class="nlc-info-value">{{ order.statusText }}</span>
        </div>
        <div class="nlc-info-row">
          <span class="nlc-info-label">Zeitraum</span>
          <span class="nlc-info-value">
            {{ order.dateFrom | date:'dd.MM.yyyy' }} - {{ order.dateTo | date:'dd.MM.yyyy' }}
          </span>
        </div>
        <div class="nlc-info-row">
          <span class="nlc-info-label">Abgerechnet</span>
          <span class="nlc-info-value">
            <mat-icon
              class="status-icon"
              [class.status-success]="order.isBilled === 1"
              [class.status-error]="order.isBilled !== 1"
              [matTooltip]="order.isBilled === 1 ? 'Ja' : 'Nein'"
              matTooltipPosition="right">
              {{ order.isBilled === 1 ? 'check_circle' : 'cancel' }}
            </mat-icon>
          </span>
        </div>
        <div class="nlc-info-row">
          <span class="nlc-info-label">Bezahlt</span>
          <span class="nlc-info-value">
            <mat-icon
              class="status-icon"
              [class.status-success]="order.isPaid === 1"
              [class.status-error]="order.isPaid !== 1"
              [matTooltip]="order.isPaid === 1 ? 'Ja' : 'Nein'"
              matTooltipPosition="right">
              {{ order.isPaid === 1 ? 'check_circle' : 'cancel' }}
            </mat-icon>
          </span>
        </div>
      </div>
    </div>

    <!-- Customer Information Card -->
    <div class="nlc-card">
      <div class="nlc-card-header">
        <div class="nlc-card-icon customer-icon">
          <mat-icon>business</mat-icon>
        </div>
        <div class="nlc-card-title-section">
          <h3>Kundeninformationen</h3>
          <p>{{ order.companyShortName }}</p>
        </div>
      </div>
      <div class="nlc-card-content">
        <div class="nlc-info-row highlight-row">
          <span class="nlc-info-label">Kundennummer</span>
          <span class="nlc-info-value">{{ order.customerNo }}</span>
        </div>
        <div class="nlc-info-row">
          <span class="nlc-info-label">Firma (Kurzname)</span>
          <span class="nlc-info-value">{{ order.companyShortName }}</span>
        </div>
        <div class="nlc-info-row">
          <span class="nlc-info-label">Firma (vollständig)</span>
          <span class="nlc-info-value">{{ order.companyName }}</span>
        </div>
        <div class="nlc-info-row">
          <span class="nlc-info-label">Kontakt</span>
          <span class="nlc-info-value">{{ order.contact }}</span>
        </div>
      </div>
    </div>

    <!-- Coin Usage Card -->
    <div class="nlc-card" *ngIf="coinUsageInfo?.found">
      <div class="nlc-card-header">
        <div class="nlc-card-icon coin-icon">
          <mat-icon>monetization_on</mat-icon>
        </div>
        <div class="nlc-card-title-section">
          <h3>Coin-Nutzung</h3>
          <p>Informationen zur Coin-Verwendung</p>
        </div>
      </div>
      <div class="nlc-card-content">
        <div class="nlc-info-row highlight-row">
          <span class="nlc-info-label">Coins verwendet (Anzahl)</span>
          <span class="nlc-info-value">{{ coinUsageInfo!.coinsUsed }}</span>
        </div>
        <div class="nlc-info-row" *ngIf="coinUsageInfo!.usedBy">
          <span class="nlc-info-label">Coins verwendet von</span>
          <span class="nlc-info-value">{{ coinUsageInfo!.usedBy }}</span>
        </div>
        <div class="nlc-info-row" *ngIf="coinUsageInfo!.usedByEmail">
          <span class="nlc-info-label">E-Mail</span>
          <span class="nlc-info-value">{{ coinUsageInfo!.usedByEmail }}</span>
        </div>
        <div class="nlc-info-row" *ngIf="coinUsageInfo!.totalCoinValue">
          <span class="nlc-info-label">Gesamtwert</span>
          <span class="nlc-info-value">{{ coinUsageInfo!.totalCoinValue | number:'1.2-2' }} €</span>
        </div>
        <div class="nlc-info-row" *ngIf="coinUsageInfo!.status">
          <span class="nlc-info-label">Status</span>
          <span class="nlc-info-value">{{ coinUsageInfo!.status }}</span>
        </div>
        <div class="nlc-info-row" *ngIf="coinUsageInfo!.createdDate">
          <span class="nlc-info-label">Erstellt am</span>
          <span class="nlc-info-value">{{ coinUsageInfo!.createdDate | date:'dd.MM.yyyy HH:mm' }}</span>
        </div>
      </div>
    </div>

    <!-- Order Comments Card -->
    <div class="comments-section">
      <app-order-comments-card
        [orderReflexNo]="order.orderNo"
        [isAdmin]="true"
        [readOnly]="true">
      </app-order-comments-card>
    </div>
  </div>

  <!-- No Data State -->
  <div *ngIf="!isLoading && !error && !(orderDetails$ | async)" class="no-data-container">
    <mat-icon class="no-data-icon">inbox</mat-icon>
    <p>Keine Auftragsdetails gefunden</p>
  </div>
</div>
